/**
 * gameReducer - Zentraler Reducer für den Spielzustand
 *
 * Verwaltet alle Spielzustandsänderungen auf vorhersagbare Weise.
 *
 * Modular aufgeteilt in Slices:
 * - shopSlice: Shop-bezogene Actions
 * - exchangeSlice: Kartentausch-Actions
 * - abilitySlice: Fähigkeiten (Shield, Peek, etc.)
 * - uiSlice: UI-State (Auswahl, Nachrichten, Splashes)
 */

import {
  shopReducer,
  isShopAction,
  exchangeReducer,
  isExchangeAction,
  abilityReducer,
  isAbilityAction,
  uiReducer,
  isUiAction,
} from './slices';

// Konstanten
const MAX_PILE_LAYERS = 6; // Maximale Anzahl sichtbarer Kartenstapel-Schichten

/**
 * Ermittelt die initiale Phase
 * Intro ist jetzt immer die Startseite (Access Control dort integriert)
 * ?skip=true → Überspringt Intro (für Entwicklung)
 */
function getInitialPhase() {
  if (typeof window !== 'undefined') {
    const params = new URLSearchParams(window.location.search);
    // Nur mit explizitem Skip überspringen (für Dev)
    if (params.get('skip') === 'true') {
      return 'gameModeSelect';
    }
  }
  // Intro ist jetzt die Standard-Startseite
  return 'intro';
}

// Initial State
export const initialGameState = {
  // Verbindungsstatus
  connected: false,
  roomId: null,
  myPlayerId: null,
  myPlayerIndex: null,

  // Spielinformationen
  phase: getInitialPhase(),
  roundNumber: 0,
  trickNumber: 0,
  numPlayers: 0,

  // Spieler
  players: [],
  currentPlayer: 0,
  rankings: [],

  // Karten
  hands: [],
  handCounts: [],
  myHand: [],
  lastPlay: [],
  lastPlayPlayer: -1,
  drawPileCount: 0,

  // Kartenstapel-Historie (für realistische Stapel-Visualisierung)
  // Jeder Eintrag: { cards: [], playerIndex, rotation: number, offsetX: number, offsetY: number }
  playHistory: [],

  // Spielregeln-Status
  isFirstTurn: true,
  canDraw: true,
  minCard: null,
  consecutivePasses: 0,

  // Wirtschaft (Arrays pro Spieler)
  coins: [],
  points: [],
  mult: [],

  // Joker & Seals
  jokers: [],
  stash: [],
  sealedCards: [],
  lastPlaySeals: [],

  // Shop
  shopJokers: [],
  shopBoosters: [],
  shopItems: [],
  shopBundlePerPlayer: [],
  shopReady: [],
  shopRerollCount: 0,
  shopJokerRerollCount: 0,
  shopTimeRemaining: 0,
  shopCurrentPlayer: 0,
  showShop: false,
  shopJokersPerPlayer: [],
  shopJokerBundlePerPlayer: [],

  // Austausch-Phase (NEW: exchangeState for sub-phase of playing)
  exchangeState: null, // { phase: 'cardCount'|'giving'|'receiving', lockedCards, pendingGivers, ... }
  exchangeReady: [],
  exchangeSelections: {},
  exchangeCurrentPlayer: 0,
  selectedCardsForExchange: [],
  showExchange: false,
  exchangeInfo: null,
  cardsPerPlayer: 13,
  exchangeCardsDealt: false,

  // Siegel-Verteilung (manuelle Phase nach Austeilen)
  sealDistribution: null, // { active, startTime, duration, readyPlayers, placements }
  sealDistributionPlacements: [], // Lokale temporäre Platzierungen [{ sealId, cardIndex }]

  // Betting ("Die Wette")
  bettingState: null,
  previousRankings: [],

  // Rundenstatistik
  roundStats: null,
  roundIncome: [],
  pendingRoundEnd: null,

  // Choreographierte Endsequenz (wenn letzte Karte gespielt wird)
  endSequence: null, // { lastCard, lastPlay, finishers, startTimestamp }
  deferredPhase: null, // Phase die nach endSequence angewendet wird

  // Shield, Peek, SealRing, Magnet, Swap & Glücksgriff
  shieldUses: [],
  peekUses: [],
  sealRingUses: [],
  magnetUses: [],
  swapUses: [],
  gluecksgriffUses: [],
  middleFingerActive: false,
  peekedCard: null,
  peekedCards: [], // For local test mode - peeked card per player (from Watcher seal)
  activePeekUntil: 0, // Timestamp until which Sneak Peak peekedCard is protected from server overwrites

  // UI-State
  selectedCards: [],

  // Animationen & Splashes
  showRoundEnd: false,
  showPlayerFinished: false,
  finishedPlayerData: null,
  finishedPlayerSplash: null,

  // Last Action Tracking
  lastAction: null,
  lastActionPlayer: null,
  lastPlaySize: 0,

  // Test Mode (wird später durch FeatureProvider gesteuert)
  isLocalTestMode: false,
  isDevAdmin: false, // Admin-User können DevTools auch in Multiplayer nutzen
  localPlayerCount: 2,
  testModeViewPlayer: null, // Which player we're viewing in test mode (null = follow currentPlayer)

  // Game Mode (Spielmodus)
  gameMode: null,           // 'firstToPoints' | 'fixedRounds' | null
  gameModeTarget: null,     // Punkteziel (50/100/500) oder Rundenanzahl (3/5/10)
  isGameComplete: false,    // Spiel-Endbedingung erreicht
  gameWinner: null,         // Index des Gewinners wenn Spiel vorbei
  winReason: null,          // 'points' | 'rounds' - Grund für Spielende

  // Turn Timer
  turnTimeLimit: null,      // Zeit in Sekunden (15/30/60 oder null = aus)
  turnStartTime: null,      // Server-Timestamp wann Zug begann

  // Private Room Progression
  levelUpNotification: null, // Level-Up Info für private Räume nach XP-Gewinn
  xpProgress: null, // XP-Fortschritt für Stammtisch-Spiele

  // Victory Votes
  votes: null, // { yes: [playerIndex, ...], no: [playerIndex, ...] }

  // Active Room (für Phase-Übergang StartScreen → Lobby)
  activeRoom: null, // { roomId, playerId, players, isHost, privateRoomStats }

  // Lobby Origin (für smart goToLobby() nach Spielende)
  lobbyOrigin: null, // 'lobby' | 'quickPlay' | 'stammtisch'

  // Host Card Back (shared deck design from host)
  hostCardBackColor: null,
  hostCardBackImage: null,
};

/**
 * Hauptreducer
 *
 * Delegiert an spezialisierte Slice-Reducer für bessere Wartbarkeit.
 */
export function gameReducer(state, action) {
  // === Delegate to Slices ===
  // Each slice handles its own subset of actions

  if (isShopAction(action.type)) {
    return shopReducer(state, action);
  }

  if (isExchangeAction(action.type)) {
    return exchangeReducer(state, action);
  }

  if (isAbilityAction(action.type)) {
    return abilityReducer(state, action);
  }

  if (isUiAction(action.type)) {
    return uiReducer(state, action);
  }

  // === Core Game Actions (handled here) ===
  switch (action.type) {
    // === Verbindung ===
    case 'SET_CONNECTION':
      return {
        ...state,
        connected: action.payload.connected,
        myPlayerId: action.payload.playerId ?? state.myPlayerId,
      };

    case 'SET_ROOM':
      return {
        ...state,
        roomId: action.payload.roomId,
        myPlayerIndex: action.payload.playerIndex ?? state.myPlayerIndex,
      };

    // === Active Room (StartScreen → Lobby Übergang) ===
    case 'SET_ACTIVE_ROOM':
      return {
        ...state,
        activeRoom: action.payload,
      };

    case 'CLEAR_ACTIVE_ROOM':
      return {
        ...state,
        activeRoom: null,
      };

    // === Lobby Origin (for smart goToLobby) ===
    case 'SET_LOBBY_ORIGIN':
      return {
        ...state,
        lobbyOrigin: action.payload,
      };

    // === Phase ===
    case 'SET_PHASE':
      return {
        ...state,
        phase: action.payload,
      };

    // === Vollständiges Server-Update ===
    case 'GAME_STATE_UPDATE':
      return mergeServerState(state, action.payload);

    // === Hand-Updates ===
    case 'UPDATE_HAND':
      return {
        ...state,
        myHand: action.payload,
      };

    case 'UPDATE_HANDS':
      return {
        ...state,
        hands: action.payload.hands,
        handCounts: action.payload.hands.map(h => h?.length ?? 0),
      };

    // === Wirtschaft ===
    case 'UPDATE_ECONOMY':
      return {
        ...state,
        coins: action.payload.coins ?? state.coins,
        points: action.payload.points ?? state.points,
        mult: action.payload.mult ?? state.mult,
      };

    case 'UPDATE_PLAYER_COINS':
      return updatePlayerArrayValue(state, 'coins', action.payload.playerIndex, action.payload.coins);

    case 'UPDATE_PLAYER_POINTS':
      return updatePlayerArrayValue(state, 'points', action.payload.playerIndex, action.payload.points);

    // === Joker ===
    case 'UPDATE_JOKERS':
      return {
        ...state,
        jokers: action.payload,
      };

    case 'UPDATE_PLAYER_JOKERS':
      return updatePlayerArrayValue(state, 'jokers', action.payload.playerIndex, action.payload.jokers);

    // === Runde ===
    case 'SET_ROUND_STATS':
      return {
        ...state,
        roundStats: action.payload,
      };

    case 'SET_PENDING_ROUND_END':
      return {
        ...state,
        pendingRoundEnd: action.payload,
      };

    // === End Sequence Actions ===
    // Note: Not in uiSlice because CLEAR_END_SEQUENCE modifies `phase` (core game state)
    // and interacts with deferredPhase logic in mergeServerState
    case 'START_END_SEQUENCE':
      {
        const endSequence = action.payload;
        const hasEndSequencePlay = Array.isArray(endSequence?.lastPlay) && endSequence.lastPlay.length > 0;
        const shouldSetLastPlay = hasEndSequencePlay && (!state.lastPlay || state.lastPlay.length === 0);
        return {
          ...state,
          endSequence,
          lastPlay: shouldSetLastPlay ? endSequence.lastPlay : state.lastPlay,
        };
      }

    case 'CLEAR_END_SEQUENCE':
      return {
        ...state,
        endSequence: null,
        phase: state.deferredPhase || state.phase,
        deferredPhase: null,
      };

    case 'SET_RANKINGS':
      console.log('[gameReducer] SET_RANKINGS called with:', JSON.stringify(action.payload), 'stack:', new Error().stack?.split('\n').slice(0,5).join(' | '));
      return {
        ...state,
        rankings: action.payload,
      };

    // === Host Card Back ===
    case 'SET_HOST_CARD_BACK':
      return {
        ...state,
        hostCardBackImage: action.payload.image,
        hostCardBackColor: action.payload.color,
      };

    // === Test Mode ===
    case 'SET_TEST_MODE':
      return {
        ...state,
        isLocalTestMode: action.payload.enabled,
        localPlayerCount: action.payload.playerCount ?? state.localPlayerCount,
      };

    case 'SET_TEST_MODE_VIEW_PLAYER': {
      // Load the new player's placements from server state
      const newPlayerIndex = action.payload;
      const serverPlacements = state.sealDistribution?.placements?.[newPlayerIndex] || [];

      return {
        ...state,
        testModeViewPlayer: newPlayerIndex,
        // Load the new player's seal placements from server state (already array format)
        sealDistributionPlacements: Array.isArray(serverPlacements) ? serverPlacements : [],
      };
    }

    // === Reset ===
    case 'RESET_GAME':
      return {
        ...initialGameState,
        connected: state.connected,
        myPlayerId: state.myPlayerId,
        isLocalTestMode: state.isLocalTestMode,
        // Game Mode bleibt erhalten für "Nochmal spielen"
        gameMode: state.gameMode,
        gameModeTarget: state.gameModeTarget,
      };

    case 'LEAVE_ROOM':
      return {
        ...initialGameState,
        connected: state.connected,
        myPlayerId: state.myPlayerId,
        isLocalTestMode: state.isLocalTestMode,
      };

    // === Game Mode ===
    case 'SET_GAME_MODE':
      return {
        ...state,
        gameMode: action.payload.gameMode,
        gameModeTarget: action.payload.gameModeTarget,
      };

    case 'SET_TURN_TIME_LIMIT':
      return {
        ...state,
        turnTimeLimit: action.payload,
      };

    case 'RESET_GAME_MODE':
      // Für "Zurück zum Menü" - setzt Game Mode komplett zurück
      return {
        ...initialGameState,
        connected: state.connected,
        myPlayerId: state.myPlayerId,
        isLocalTestMode: state.isLocalTestMode,
        // Game Mode wird zurückgesetzt
        gameMode: null,
        gameModeTarget: null,
        isGameComplete: false,
        gameWinner: null,
        winReason: null,
      };

    default:
      console.warn(`Unknown action type: ${action.type}`);
      return state;
  }
}

// === Helper Functions ===

/**
 * Prüft ob zwei lastPlay Arrays unterschiedlich sind
 */
function lastPlayChanged(oldPlay, newPlay) {
  if (!oldPlay && !newPlay) return false;
  if (!oldPlay || !newPlay) return true;
  if (oldPlay.length !== newPlay.length) return true;
  if (oldPlay.length === 0) return false;

  // Vergleiche erste Karte (reicht für Änderungserkennung)
  const oldFirst = oldPlay[0];
  const newFirst = newPlay[0];
  return oldFirst?.rank !== newFirst?.rank || oldFirst?.suit !== newFirst?.suit;
}

/**
 * Generiert deterministische Rotation und Offset für realistische Stapel-Optik
 * Verwendet Kartendaten als Seed für reproduzierbare "Zufälligkeit" (pure function)
 */
function generatePileTransform(cards, playerIndex) {
  // Einfacher Hash aus Kartendaten für deterministische Pseudo-Zufälligkeit
  const seed = cards.reduce((acc, c) => acc + (c.value || 0) + c.suit.charCodeAt(0), playerIndex * 100);
  const hash = (seed * 9301 + 49297) % 233280;
  const rnd = hash / 233280; // 0-1

  return {
    rotation: (rnd - 0.5) * 16,                           // -8° bis +8°
    offsetX: ((hash * 7) % 233280 / 233280 - 0.5) * 10,   // -5px bis +5px
    offsetY: ((hash * 13) % 233280 / 233280 - 0.5) * 6,   // -3px bis +3px
  };
}

/**
 * Aktualisiert die playHistory basierend auf neuem lastPlay
 */
function updatePlayHistory(currentHistory, currentLastPlay, newLastPlay, newLastPlayPlayer) {
  // Stich-Reset: lastPlay ist leer → Historie löschen
  if (!newLastPlay || newLastPlay.length === 0) {
    return [];
  }

  // Keine Änderung
  if (!lastPlayChanged(currentLastPlay, newLastPlay)) {
    return currentHistory;
  }

  // Neuer Zug: Zur Historie hinzufügen mit deterministischer Transformation
  const transform = generatePileTransform(newLastPlay, newLastPlayPlayer);
  const newEntry = {
    cards: newLastPlay,
    playerIndex: newLastPlayPlayer,
    ...transform,
  };

  // Max Einträge behalten (älteste raus)
  const updatedHistory = [...currentHistory, newEntry];
  if (updatedHistory.length > MAX_PILE_LAYERS) {
    return updatedHistory.slice(-MAX_PILE_LAYERS);
  }
  return updatedHistory;
}

/**
 * Merged Server State mit lokalem State
 */
function mergeServerState(state, serverState) {
  if (!serverState) return state;

  // DEBUG: Log rankings changes during playing phase
  if (serverState.phase === 'playing' && serverState.rankings) {
    console.log('[gameReducer] DEBUG rankings from server:', JSON.stringify(serverState.rankings), 'current state rankings:', JSON.stringify(state.rankings));
  }

  // CRITICAL: Detect new partie start and clear stale pendingRoundEnd
  // A new partie is starting when:
  // 1. Server sends phase 'playing' with empty rankings (fresh partie)
  // 2. We have a pendingRoundEnd from the previous partie
  // This prevents old rankings from being applied during the new partie
  const isNewPartie = serverState.phase === 'playing' &&
                      Array.isArray(serverState.rankings) &&
                      serverState.rankings.length === 0 &&
                      state.pendingRoundEnd;

  if (isNewPartie) {
    console.log('[gameReducer] New partie detected - clearing pendingRoundEnd to prevent stale rankings');
  }

  // CRITICAL: Block lastPlay/playHistory clearing while endSequence is active
  // The end sequence needs the final cards to remain visible during CARD_LINGER phase.
  // Without this, the server's post-reset state (lastPlay: []) wipes the display.
  const blockLastPlayClear = state.endSequence &&
    serverState.lastPlay !== undefined &&
    serverState.lastPlay.length === 0 &&
    state.lastPlay.length > 0;

  // Berechne neue playHistory nur wenn lastPlay vom Server gesendet wurde
  // (undefined = nicht gesendet, [] = explizit leer/reset)
  const newPlayHistory = blockLastPlayClear
    ? state.playHistory
    : (serverState.lastPlay !== undefined
      ? updatePlayHistory(
          state.playHistory,
          state.lastPlay,
          serverState.lastPlay,
          serverState.lastPlayPlayer ?? state.lastPlayPlayer
        )
      : state.playHistory);

  // CRITICAL: Block phase change to 'roundEnd' while endSequence is active
  // The choreographed end sequence must complete before transitioning phases
  // Otherwise PlayingPhase unmounts and the animation is interrupted
  let newPhase = serverState.phase ?? state.phase;
  let deferredPhase = state.deferredPhase;
  if (state.endSequence && serverState.phase === 'roundEnd') {
    deferredPhase = 'roundEnd'; // Store for later
    newPhase = state.phase; // Keep current phase
  }

  return {
    ...state,
    // Spielinfo
    phase: newPhase,
    deferredPhase, // Phase die nach endSequence angewendet wird
    roundNumber: serverState.roundNumber ?? state.roundNumber,
    trickNumber: serverState.trickNumber ?? state.trickNumber,
    numPlayers: serverState.numPlayers ?? state.numPlayers,

    // Spieler
    players: serverState.players ?? serverState.playerSummaries ?? state.players,
    currentPlayer: serverState.currentPlayer ?? state.currentPlayer,
    rankings: serverState.rankings ?? state.rankings,

    // Karten
    hands: serverState.hands ?? state.hands,
    handCounts: serverState.handCounts ?? state.handCounts,
    myHand: serverState.myHand ?? state.myHand,
    lastPlay: blockLastPlayClear ? state.lastPlay : (serverState.lastPlay ?? state.lastPlay),
    lastPlayPlayer: serverState.lastPlayPlayer ?? state.lastPlayPlayer,
    drawPileCount: serverState.drawPileCount ?? state.drawPileCount,
    playHistory: newPlayHistory,

    // Spielregeln
    isFirstTurn: serverState.isFirstTurn ?? state.isFirstTurn,
    canDraw: serverState.canDraw ?? state.canDraw,
    minCard: serverState.minCard ?? state.minCard,
    consecutivePasses: serverState.consecutivePasses ?? state.consecutivePasses,

    // Wirtschaft
    coins: serverState.coins ?? state.coins,
    points: serverState.points ?? state.points,
    mult: serverState.mult ?? state.mult,

    // Joker & Seals
    jokers: serverState.jokers ?? state.jokers,
    stash: serverState.stash ?? state.stash,
    sealedCards: serverState.sealedCards ?? state.sealedCards,
    lastPlaySeals: serverState.lastPlaySeals ?? state.lastPlaySeals,

    // Shop
    shopJokers: serverState.shopJokers ?? state.shopJokers,
    shopBoosters: serverState.shopBoosters ?? state.shopBoosters,
    shopItems: serverState.shopItems ?? state.shopItems,
    shopBundlePerPlayer: serverState.shopBundlePerPlayer ?? state.shopBundlePerPlayer,
    shopReady: serverState.shopReady ?? state.shopReady,
    shopRerollCount: serverState.shopRerollCount ?? state.shopRerollCount,
    shopJokerRerollCount: serverState.shopJokerRerollCount ?? state.shopJokerRerollCount,
    shopCurrentPlayer: serverState.shopCurrentPlayer ?? state.shopCurrentPlayer,
    shopJokersPerPlayer: serverState.shopJokersPerPlayer ?? state.shopJokersPerPlayer,
    shopJokerBundlePerPlayer: serverState.shopJokerBundlePerPlayer ?? state.shopJokerBundlePerPlayer,

    // Austausch (NEW: exchangeState for sub-phase of playing)
    exchangeState: serverState.exchangeState ?? state.exchangeState,
    exchangeReady: serverState.exchangeReady ?? state.exchangeReady,
    exchangeSelections: serverState.exchangeSelections ?? state.exchangeSelections,
    exchangeCurrentPlayer: serverState.exchangeCurrentPlayer ?? state.exchangeCurrentPlayer,
    exchangeCardsDealt: serverState.exchangeCardsDealt ?? state.exchangeCardsDealt,
    cardsPerPlayer: serverState.cardsPerPlayer ?? state.cardsPerPlayer,

    // Siegel-Verteilung
    sealDistribution: serverState.sealDistribution ?? state.sealDistribution,
    // Lokale Placements werden nicht vom Server überschrieben
    sealDistributionPlacements: state.sealDistributionPlacements,

    // Statistiken
    roundStats: serverState.roundStats ?? state.roundStats,
    roundIncome: serverState.roundIncome ?? state.roundIncome,

    // Clear stale pendingRoundEnd on new partie start
    pendingRoundEnd: isNewPartie ? null : state.pendingRoundEnd,
    // Also clear endSequence to prevent any lingering animation state
    endSequence: isNewPartie ? null : state.endSequence,

    // Shield, Peek, SealRing, Magnet & Swap
    shieldUses: serverState.shieldUses ?? state.shieldUses,
    peekUses: serverState.peekUses ?? state.peekUses,
    sealRingUses: serverState.sealRingUses ?? state.sealRingUses,
    magnetUses: serverState.magnetUses ?? state.magnetUses,
    swapUses: serverState.swapUses ?? state.swapUses,
    gluecksgriffUses: serverState.gluecksgriffUses ?? state.gluecksgriffUses,
    middleFingerActive: serverState.middleFingerActive ?? state.middleFingerActive,

    // Watcher seal peek (from Beobachter HAND seal)
    // WICHTIG: null ist ein gültiger Wert (= Siegel entfernt, keine Karte mehr sichtbar)
    // ?? würde null als nullish behandeln und den alten Wert beibehalten
    // BUT: Don't overwrite if an active Sneak Peak is in progress (protected by activePeekUntil timestamp)
    peekedCard: (state.activePeekUntil > Date.now())
      ? state.peekedCard  // Keep client-side Sneak Peak value
      : (serverState.peekedCard !== undefined ? serverState.peekedCard : state.peekedCard),
    peekedCards: serverState.peekedCards !== undefined ? serverState.peekedCards : state.peekedCards,
    // Preserve activePeekUntil (client-side only, not from server)
    activePeekUntil: state.activePeekUntil,

    // Player-spezifisch
    myPlayerIndex: serverState.myPlayerIndex ?? state.myPlayerIndex,

    // Test Mode
    isLocalTestMode: serverState.isLocalTestMode ?? state.isLocalTestMode,
    isDevAdmin: serverState.isDevAdmin ?? state.isDevAdmin,
    // Keep testModeViewPlayer - it's client-side only
    testModeViewPlayer: state.testModeViewPlayer,

    // Game Mode
    gameMode: serverState.gameMode ?? state.gameMode,
    gameModeTarget: serverState.gameModeTarget ?? state.gameModeTarget,
    isGameComplete: serverState.isGameComplete ?? state.isGameComplete,
    gameWinner: serverState.gameWinner ?? state.gameWinner,
    winReason: serverState.winReason ?? state.winReason,

    // Turn Timer (null = explicitly off, so use !== undefined)
    turnTimeLimit: serverState.turnTimeLimit !== undefined ? serverState.turnTimeLimit : state.turnTimeLimit,
    turnStartTime: serverState.turnStartTime !== undefined ? serverState.turnStartTime : state.turnStartTime,

    // Private Room Progression
    levelUpNotification: serverState.levelUpNotification ?? state.levelUpNotification,
    xpProgress: serverState.xpProgress ?? state.xpProgress,

    // Victory Votes
    votes: serverState.votes ?? state.votes,

    // Host Card Back (color from broadcast, image via separate event)
    // When color changes (new setter), clear stale image — fresh image arrives via hostCardBack event
    hostCardBackColor: serverState.hostCardBackColor !== undefined ? serverState.hostCardBackColor : state.hostCardBackColor,
    hostCardBackImage: (serverState.hostCardBackColor !== undefined && serverState.hostCardBackColor !== state.hostCardBackColor)
      ? null
      : state.hostCardBackImage,

    // Betting ("Die Wette")
    bettingState: serverState.bettingState !== undefined ? serverState.bettingState : state.bettingState,
    previousRankings: serverState.previousRankings !== undefined ? serverState.previousRankings : state.previousRankings,
  };
}

/**
 * Wert in einem Spieler-Array updaten
 */
function updatePlayerArrayValue(state, arrayName, playerIndex, value) {
  const newArray = [...state[arrayName]];
  newArray[playerIndex] = value;
  return {
    ...state,
    [arrayName]: newArray,
  };
}

export default gameReducer;
